<template>
  <div v-if="loading" class="page-center">
    <ag-loading />
  </div>
  <div v-else class="recipe-view">
    <div class="hero" :class="{ 'has-image': recipe.imageUrl }">
      <div class="header">
        <div class="wrapper">
          <button class="back-button" @click="goBack">
            <ag-icon width="42" height="42">
              <chevron-left-icon />
            </ag-icon>
          </button>
          <button @click="goHome">
            <logo white logoHeight="64" />
          </button>
        </div>
      </div>
      <img class="recipe-image" v-if="recipe.imageUrl" :src="recipe.imageUrl" :alt="recipe.name" />
      <div class="hero-content">
        <div class="wrapper">
          <h1>{{ recipe.name }}</h1>
          <h3>{{ recipe.categoryKey }}</h3>
          <p v-if="recipe.description">{{ recipe.description }}</p>
          <div class="metrics" v-if="recipe.prepTime || recipe.cookTime || recipe.servings">
            <div v-if="recipe.prepTime">
              <strong>{{ recipe.prepTime }}</strong>
              <span>Prep</span>
            </div>
            <div v-if="recipe.cookTime">
              <strong>{{ recipe.cookTime }}</strong>
              <span>Cook</span>
            </div>
            <div v-if="recipe.servings">
              <strong>{{ recipe.servings }}</strong>
              <span>Serve</span>
            </div>
          </div>
          <div class="scroll-down">
            <button @click="scrollTop">
              <ag-icon width="42" height="42">
                <chevron-down-icon />
              </ag-icon>
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="recipe-content" ref="recipe">
      <img class="recipe-image " v-if="recipe.imageUrl" :src="recipe.imageUrl" :alt="recipe.name" />
      <div class="ingredients" v-if="recipe.ingredients.length">
        <h2>Ingredients</h2>
        <div class="ingredient-sets">
          <div class="ingredient-set" v-for="(ingredientSet, index) in recipe.ingredients" :key="`${ingredientSet.title}-${index}`">
            <Ingredients
              :ingredients="ingredientSet"
              :setIndex="index"
              :displayTitle="recipe.ingredients.length > 1"
              :viewOnly="true"
            />
          </div>
        </div>
      </div>
      <div v-if="recipe.directions || recipe.hints">
        <h2 v-if="recipe.directions">Directions</h2>
        <h2 v-else-if="recipe.hints">Notes</h2>
        <div class="wrapper">
          <span v-if="recipe.directions" class="directions">{{ recipe.directions }}</span>
          <Hints
            v-if="recipe.hints.length"
            :hints="recipe.hints"
            :viewOnly="true"
          />
        </div>
      </div>
      <div class="wrapper">
        <div class="recipe-meta">
          <added-by
            v-if="recipe.userId || recipe.source || recipe.sourceUrl"
            :id="recipe.userId"
            :source="recipe.source"
            :sourceUrl="recipe.sourceUrl"
          />
        </div>
      </div>

      <!-- Tags -->
      <div class="tags dark-module" v-if="recipe.tags.length">
        <label>Tagged With:</label>
        <ag-tag
          v-for="tag in recipe.tags"
          :key="tag.id"
          :id="tag.id"
          :name="tag.name"
          :darkTheme="true"
          :viewOnly="true"
        />
      </div>

      <!-- Related Items -->
      <div class="related-items dark-module" v-if="recipe.relatedItems.length">
        <label>You may also like:</label>
        <related-item
          v-for="relatedItem in recipe.relatedItems"
          :key="relatedItem.key"
          :recipeKey="relatedItem.key"
          :categoryKey="relatedItem.categoryKey"
          :name="relatedItem.name"
          :imageUrl="relatedItem.image_url"
        />
      </div>
    </div>
    <div class="wrapper admin" v-if="isAuthenticated">
      <button @click="routeToEdit">Edit Recipe</button>
    </div>
  </div>
</template>

<script>
  import Hints from "./components/Hints";
  import Logo from "@/components/Logo";
  import RelatedItem from "./components/RelatedItem";
  import Ingredients from "./components/Ingredients";
  import AddedBy from "./components/AddedBy";
  import { ChevronDownIcon, ChevronLeftIcon } from "vue-feather-icons";
  import { mapState } from "vuex";
  import { pageTitleBase } from "../../router";

  export default {
    name: "RecipeView",
    data: () => ({
      loading: true,
      recipe: {
        id: null,
        name: null,
        key: null,
        description: null,
        categoryKey: null,
        source: null,
        sourceUrl: null,
        prepTime: null,
        cookTime: null,
        directions: null,
        servings: null,
        hints: [],
        ingredients: [],
        userId: null,
        relatedItems: [],
        imageUrl: null,
        tags: [],
      },
      breadcrumbs: ["Recipes"]
    }),
    async mounted() {
      const { categoryKey, key } = this.$route.params;
      try {
        const result = await this.$service.recipe.findByKeys(categoryKey, key);

        this.recipe = {
          ...this.recipe,
          ...result
        };

        if (this.recipe.source || this.recipe.sourceUrl) {
          this.sourceToggle = true;
        }

        if (this.recipe.ingredients === null) {
          this.recipe.ingredients = [];
        }

        if(this.recipe.name) {
          document.title = `${this.recipe.name} | ${pageTitleBase}`;
        }

        if(this.recipe.id) {
          try {
            const tags = await this.$service.recipe.findTagsByRecipeId(this.recipe.id);
            this.recipe.tags = tags;
          } catch (e) {
            console.error(`Error loading tags for recipe id [${this.recipe.id}]`, e);
          }

          try {
            const relatedItems = await this.$service.recipe.findRelatedRecipesByRecipeId(this.recipe.id);
            this.recipe.relatedItems = relatedItems;
          } catch (e) {
            console.error(`Error loading related recipes for recipe with id [${this.recipe.id}]`, e);
          }
        }
      } catch (e) {
        console.error(`Error loading recipe by keys [${categoryKey}, ${key}]`, e);
        // TODO Display reason in global error component
      }

      this.loading = false;
    },
    computed: {
      ...mapState("user", ["isAuthenticated"])
    },
    methods: {
      routeToEdit() {
        this.$router.push({
          name: "RecipeDetail",
          params: { 
            categoryKey: this.recipe.categoryKey,
            key: this.recipe.key
           }
        });
      },
      scrollTop: function () {
        const elementPosition = this.$refs.recipe.offsetTop;
        this.intervalId = setInterval(() => {
          if (window.pageYOffset >= elementPosition - 20) {
            clearInterval(this.intervalId);
          }
          const amtLeftToScroll = elementPosition - window.pageYOffset;
          const scrollInterval = 20;
          const amtToScroll = amtLeftToScroll < scrollInterval ? amtLeftToScroll : scrollInterval;
          window.scroll(0, window.pageYOffset + amtToScroll);
        }, 15);
      },
      goBack: function() {
        this.$router.go(-1);
      },
      goHome: function() {
        this.$router.push({ name: "Recipes" });
      }
    },
    components: { Hints, Logo, Ingredients, AddedBy, ChevronDownIcon, ChevronLeftIcon, RelatedItem }
  };
</script>

<style scoped lang="scss">
  #app .app-content .router-outlet > .recipe-view {
    padding: 0;
  }

  .wrapper {
    padding: $gutter;
    line-height: 1.6em;
  }

  .recipe-view {
    font-size: 16px;

    h1,
    h2,
    h3 {
      font-size: 32px;
      text-transform: uppercase;
      margin-bottom: 0;
      text-shadow: 0 2px 8px rgba(0,0,0,0.3);
      color: #fff;
    }

    h2 {
      background: $color-purple-light;
      line-height: 1.1em;
      font-size: 18px;
      text-align: center;
      padding: 16px $gutter;
      text-shadow: none;
    }

    h3 {
      font-size: 16px;
      border: 0;
      color: #bbb;
    }

    .recipe-image {
      display: block;
      width: 100%;
    }

    .hero {
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      background: #000;
      color: #aaa;
      font-size: 14px;
      position: relative;
      padding-top: 100px; //height of header

      &.has-image {
        height: calc(100vh - 100px); //height of header
        overflow: hidden;

        .recipe-image {
          transform: translate(-50%, -50%);

          @media (max-width: 550px) {
            transform: translate(-50%, -50%) scale(1.6);
          }
        }
      }

      .header {
        width: 100%;
        position: absolute;
        top: 0;
        left: 0;
        z-index: 2;
        margin: 0 auto;
        background-image: linear-gradient(rgba(0,0,0,0.5), transparent);

        .wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 8px $gutter;
          max-width: 500px;
          width: 100%;
          box-sizing: border-box;
          margin: 0 auto;
          position: relative;
        }

        .back-button {
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
        }
      }

      .recipe-image {
        width: 100%;
        min-height: 100vh;
        object-fit: cover;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        z-index: 1;
        transition: all 0.3s ease-in;
      }

      .hero-content {
        position: relative;
        z-index: 3;
        background-image: linear-gradient(transparent, rgba(0,0,0,0.7) 50%);
        padding: $gutter $gutter 0 $gutter;

        .wrapper {
          margin: 0 auto;
          width: 100%;
          max-width: 500px;
          padding: 0;
        }

        p {
          font-style: oblique;
          margin: 8px 0 0 0;
        }

        .metrics {
          display: flex;
          align-items: center;
          text-transform: uppercase;
          margin: $gutter 0 0 0;
          div {
            display: flex;
            flex-direction: column;
            text-align: center;
            color: $color-gray;
            flex-grow: 1;
            text-shadow: 0 2px 8px rgba(0,0,0,0.6);

            strong {
              color: $color-white;
              font-size: 26px;
              line-height: 1.4em;
            }
          }
        }

        .scroll-down {
          text-align: center;
          position: relative;

          &:before,
          &:after {
            content: '';
            position: absolute;
            top: 50%;
            left: 0;
            transform: translateY(-50%);
            width: 42%;
            height: 1px;
            background: #bbb;
          }

          &:after {
            left: auto;
            right: 0;
          }
        }
      }
    }

    .scroll-down,
    .header {
      button {
        background: transparent;
        min-width: 0;
        padding: 10px;
        width: 100%;
        box-shadow: none;
        position: relative;
        z-index: 1;

        &:hover {
          svg {
            stroke: $color-secondary;
          }
        }
      }
    }

    .header {
      button {
        width: auto;
      }
    }

    @media (min-width: 501px) {
      .recipe-content {
        .recipe-image {
          display: none;
        }
      }
    }

    .ingredient-set {
      padding: $gutter;

      + .ingredient-set {
        padding-top: 0;
      }
    }

    .recipe-meta {
      border-top: 1px solid #eee;
      padding-top: 10px;
    }

    .dark-module {
      background: #000;
      color: #bbb;
      padding: $gutter $gutter 10px $gutter;
      color: #bbb;

      .tag {
        margin: 0 $gutter 10px 0;
      }

      label {
        font-size: 16px;
        text-transform: uppercase;
        font-weight: 700;
        display: block;
        margin-bottom: 8px;
      }

      &.related-items {
        padding: 0;

        label {
          padding: $gutter $gutter 0 $gutter;
        }
      }
    }

    .admin {
      button {
        display: block;
        width: 100%;
      }
    }
  }
</style>
